import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import SeverityBadge from '../components/SeverityBadge';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { alertsApi } from '../lib/api';

function formatTimestamp(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [severity, setSeverity] = useState('all');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState(() =>
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const loadAlerts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await alertsApi.list({ page: 1, page_size: 200 });
      setAlerts(response.data?.items || []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load alerts.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [severity, status, dateFrom, dateTo]);

  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      if (severity !== 'all' && alert.severity !== severity) return false;
      if (status !== 'all' && alert.status !== status) return false;
      const ts = new Date(alert.ts);
      if (dateFrom && ts < new Date(dateFrom)) return false;
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (ts > end) return false;
      }
      return true;
    });
  }, [alerts, severity, status, dateFrom, dateTo]);

  const openCount = filteredAlerts.filter((alert) => alert.status === 'open').length;
  const ackCount = filteredAlerts.filter((alert) => alert.status === 'ack').length;
  const resolvedCount = filteredAlerts.filter((alert) => alert.status === 'resolved').length;
  const paginatedAlerts = filteredAlerts.slice((page - 1) * pageSize, page * pageSize);

  const mutateAlert = async (id, action) => {
    try {
      if (action === 'ack') {
        await alertsApi.ack(id, '');
      } else {
        await alertsApi.resolve(id, '');
      }
      toast.success(action === 'ack' ? 'Alert acknowledged.' : 'Alert resolved.');
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update alert.');
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="alerts-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Alerts</h1>
        <p className="mt-2 text-slate-500">
          Triage active incidents, review severity, and manage acknowledgment or resolution state.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Open</p>
            <p className="mt-2 text-3xl font-bold text-amber-400">{openCount}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Acknowledged</p>
            <p className="mt-2 text-3xl font-bold text-blue-400">{ackCount}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Resolved</p>
            <p className="mt-2 text-3xl font-bold text-emerald-400">{resolvedCount}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Alert Queue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="border-slate-600 bg-slate-700 text-white">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="border-slate-600 bg-slate-700 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="ack">Acknowledged</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : paginatedAlerts.length ? (
            <DataTable
              columns={[
                {
                  key: 'severity',
                  label: 'Severity',
                  render: (value) => <SeverityBadge severity={value} />,
                },
                {
                  key: 'type',
                  label: 'Type',
                  render: (value) => String(value || '').replace(/_/g, ' '),
                },
                {
                  key: 'employee_name',
                  label: 'Employee',
                  render: (value) => value || 'System',
                },
                {
                  key: 'device_hostname',
                  label: 'Device',
                  render: (value) => value || '—',
                },
                {
                  key: 'ts',
                  label: 'Timestamp',
                  render: (value) => formatTimestamp(value),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (value) => <StatusBadge status={value || 'open'} />,
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex gap-2">
                      {row.status === 'open' ? (
                        <Button
                          size="sm"
                          className="bg-blue-600 text-white hover:bg-blue-700"
                          onClick={(event) => {
                            event.stopPropagation();
                            mutateAlert(row.id, 'ack');
                          }}
                        >
                          Acknowledge
                        </Button>
                      ) : null}
                      {row.status !== 'resolved' ? (
                        <Button
                          size="sm"
                          className="bg-red-600 text-white hover:bg-red-700"
                          onClick={(event) => {
                            event.stopPropagation();
                            mutateAlert(row.id, 'resolve');
                          }}
                        >
                          Resolve
                        </Button>
                      ) : null}
                    </div>
                  ),
                },
              ]}
              data={paginatedAlerts}
              loading={loading}
              emptyMessage="No alerts match the current filters."
              pagination={{
                page,
                pageSize,
                total: filteredAlerts.length,
                onPageChange: setPage,
              }}
            />
          ) : (
            <EmptyState
              icon={<AlertTriangle className="h-8 w-8" />}
              title="No alerts"
              description="No alerts match the selected filters or date range."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
